import {
  Ethnicity,
  Gender,
  MaritalStatus,
  PatientStatus,
  Relationship,
} from "../services/utils";
import { Appointment, AppointmentChecklist } from "./admissions.model";
import { Coverage } from "./coverages.model";
import { ChecklistQuestionsCache, Provider } from "./lookup.model";
import { OldLabInfo } from "./old-labs.model";
import { PatientDoc } from "./patient-docs.model";
import { PriorAuth } from "./prior-auth.model";
import { EncounterSchedule, Procedure } from "./procedure.model";
import { ReferralChecklist } from "./referral-checklist.model";

export class Patient {
  id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dob: string;
  gender: Gender | string = "";
  address: string;
  city?: string;
  state?: string;
  zip: string;
  phone: string;
  email?: string;
  physicianName?: string;
  lastAppt?: string;
  addressLine2?: string;
  apartmentNumber?: string;
  upcomingAppt?: string;
  status?: PatientStatus = PatientStatus.Admitted;
  emergencyContactName: string;
  emergencyContactNumber: string;
  emergencyContactRelation?: Relationship | string = "";
  maritalStatus?: MaritalStatus | string = "";
  ethnicity?: Ethnicity | string = "";
  insuranceInformation?: string;
  pharmacyName: string;
  pharmacyPhone: string;
  pharmacyFax: string;
  pharmacyAddress: string;
  pharmacyCity: string;
  pharmacyState: string;
  pharmacyZip: string;
  primaryCareName?: string;
  primaryCarePhone?: string;
  primaryCareAddress?: string;
  primaryCareCity?: string;
  primaryCareState?: string;
  primaryCareZip?: string;
  referringPhysician?: string;
  /** Engage / list-clinic referral id when linked */
  referralId?: string;
  referralSource?: string;
  service?: string;
  mrn?: string;
  advocateName?: string;
  advocateDob?: string;
  age?: number;
  dOS?: string;
  socialSecurityNumber?: string;
  consentAndAuthorization?: string;
  nextOfKin?: string;
  imageData?: PatientImage;
  constructor() {}
}

/** PHI patient columns edited in demographics (preferred pharmacy + PCP). */
export const PATIENT_PHARMACY_FIELD_KEYS = [
  "pharmacyName",
  "pharmacyPhone",
  "pharmacyFax",
  "pharmacyAddress",
  "pharmacyCity",
  "pharmacyState",
  "pharmacyZip",
] as const;

export const PATIENT_PRIMARY_CARE_FIELD_KEYS = [
  "primaryCareName",
  "primaryCarePhone",
  "primaryCareAddress",
  "primaryCareCity",
  "primaryCareState",
  "primaryCareZip",
] as const;

/** Keeps saved pharmacy / PCP on the patient cache when PATCH responses omit them. */
export function mergePatientPharmacyAndPrimaryCareFromSave(
  base: Patient,
  saved: Partial<Patient>
): Patient {
  const next = { ...base };
  for (const key of PATIENT_PHARMACY_FIELD_KEYS) {
    if (saved[key] !== undefined) {
      next[key] = saved[key] ?? "";
    }
  }
  for (const key of PATIENT_PRIMARY_CARE_FIELD_KEYS) {
    if (saved[key] !== undefined) {
      next[key] = saved[key] ?? "";
    }
  }
  return next;
}

export class UpsertPatient {
  id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dob: string;
  gender?: Gender | string = "";
  address: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip: string;
  phone: string;
  email?: string;
  emergencyContactName: string;
  emergencyContactNumber: string;
  emergencyContactRelation?: Relationship | string = "";
  maritalStatus?: MaritalStatus | string = "";
  ethnicity?: Ethnicity | string = "";
  socialSecurityNumber?: string;
  pharmacyName: string;
  pharmacyPhone: string;
  pharmacyFax: string;
  pharmacyAddress: string;
  pharmacyCity: string;
  pharmacyState: string;
  pharmacyZip: string;
  primaryCareName?: string;
  primaryCarePhone?: string;
  primaryCareAddress?: string;
  primaryCareCity?: string;
  primaryCareState?: string;
  primaryCareZip?: string;
  imageData?: PatientImage;
  /** Present when saving from Engage (PATCH includes referral context). */
  referralId?: string;
  constructor(patientInfo: Patient) {
    this.id = patientInfo.id;
    this.firstName = patientInfo.firstName;
    this.middleName = patientInfo.middleName;
    this.lastName = patientInfo.lastName;
    this.dob = patientInfo.dob;
    this.gender = patientInfo.gender;
    this.address = patientInfo.address;
    this.addressLine2 = patientInfo.addressLine2;
    this.city = patientInfo.city;
    this.state = patientInfo.state;
    this.zip = patientInfo.zip;
    this.phone = patientInfo.phone;
    this.email = patientInfo.email;
    this.emergencyContactName = patientInfo.emergencyContactName;
    this.emergencyContactNumber = patientInfo.emergencyContactNumber;
    this.emergencyContactRelation = patientInfo.emergencyContactRelation;
    this.maritalStatus = patientInfo.maritalStatus;
    this.ethnicity = patientInfo.ethnicity;
    this.socialSecurityNumber = patientInfo.socialSecurityNumber;
    this.imageData = patientInfo.imageData;
    this.pharmacyName = patientInfo.pharmacyName;
    this.pharmacyPhone = patientInfo.pharmacyPhone;
    this.pharmacyFax = patientInfo.pharmacyFax;
    this.pharmacyAddress = patientInfo.pharmacyAddress;
    this.pharmacyCity = patientInfo.pharmacyCity;
    this.pharmacyState = patientInfo.pharmacyState;
    this.pharmacyZip = patientInfo.pharmacyZip;
    this.primaryCareName = patientInfo.primaryCareName;
    this.primaryCarePhone = patientInfo.primaryCarePhone;
    this.primaryCareAddress = patientInfo.primaryCareAddress;
    this.primaryCareCity = patientInfo.primaryCareCity;
    this.primaryCareState = patientInfo.primaryCareState;
    this.primaryCareZip = patientInfo.primaryCareZip;
    this.referralId = patientInfo.referralId;
  }
}

export interface PatientSearchResult {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  dob: string;
}

export interface PatientImage {
  base64String: string;
  contentType: string;
}

/** `referralSource` on PHI patient API — id string or `{ id, name, … }`. */
export type PhiPatientReferralSource = string | Record<string, unknown>;

/**
 * PHI `POST/PATCH/GET api/v1/patients` payload/response (Swagger: worklist create + Engage-linked fields).
 * Write body includes `referralSource` (object), `service`, `formProduct`, `queId`, `leadStatus`, `formStatus`, etc.
 */
export interface PhiPatientApiRecord {
  id?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  address?: string;
  pharmacyName?: string;
  pharmacyPhone?: string;
  pharmacyFax?: string;
  pharmacyAddress?: string;
  pharmacyCity?: string;
  pharmacyState?: string;
  pharmacyZip?: string;
  primaryCareName?: string;
  primaryCarePhone?: string;
  primaryCareAddress?: string;
  primaryCareCity?: string;
  primaryCareState?: string;
  primaryCareZip?: string;
  apartmentNumber?: string;
  phone?: string;
  email?: string;
  maritalStatus?: string;
  ethnicity?: string;
  socialSecurityNumber?: string;
  city?: string;
  state?: string;
  zip?: string;
  status?: string;
  addressLine2?: string;
  dOS?: string;
  physicianName?: string;
  secondaryPhone?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  emergencyContactRelation?: string;
  imageData?: PatientImage;
  referralId?: string;
  queId?: string;
  /** Some PHI gateways bind snake_case `que_id` on write. */
  que_id?: string;
  leadStatus?: string;
  /** Pipeline status on patient row (Lead / Prospect / Established). */
  patientStatus?: string;
  /** Some PHI gateways bind snake_case `patient_status` on write. */
  patient_status?: string;
  associatedType?: string;
  conversationSid?: string;
  participantSid?: string;
  assignedTo?: string;
  firstResponse?: string;
  formStatus?: string;
  formProduct?: string;
  service?: string;
  referralSource?: PhiPatientReferralSource;
  /** Some PHI gateways bind `referral_source` on write while directory reads `referralSource`. */
  referral_source?: string;
  isBusinessHours?: boolean;
  ghlContactId?: string;
  tagSlug?: string;
  /** Anthropometrics — `PATCH api/v1/patients/{id}` (e.g. `5'6"`, `150 lbs`). */
  height?: string;
  weight?: string;
  measuredOn?: string;
}

/** Body for patient biometrics update (`PATCH api/v1/patients/{id}`). */
export interface PatientBiometricsPatch {
  height: string;
  weight: string;
  measuredOn: string;
}

export type PhiPatientApiResponse = Patient & PhiPatientApiRecord;

function looksLikePhiPatientRecord(o: Record<string, unknown>): boolean {
  return (
    o["id"] != null ||
    o["patientId"] != null ||
    o["patient_id"] != null ||
    o["firstName"] != null ||
    o["lastName"] != null ||
    o["referralId"] != null
  );
}

function phiPatientRecordFromUnknown(raw: unknown): Record<string, unknown> {
  if (raw == null) {
    return {};
  }
  if (Array.isArray(raw)) {
    const first = raw[0];
    return first && typeof first === "object"
      ? phiPatientRecordFromUnknown(first)
      : {};
  }
  if (typeof raw !== "object") {
    return {};
  }
  const o = raw as Record<string, unknown>;
  if (looksLikePhiPatientRecord(o)) {
    return o;
  }
  const bodyNest = o["body"];
  if (bodyNest != null && typeof bodyNest === "object" && !Array.isArray(bodyNest)) {
    const inner = phiPatientRecordFromUnknown(bodyNest);
    if (looksLikePhiPatientRecord(inner)) {
      return inner;
    }
  }
  for (const key of [
    "data",
    "patient",
    "result",
    "record",
    "patientRecord",
    "createdPatient",
  ]) {
    const nest = o[key];
    if (nest && typeof nest === "object" && !Array.isArray(nest)) {
      const inner = phiPatientRecordFromUnknown(nest);
      if (looksLikePhiPatientRecord(inner)) {
        return inner;
      }
    }
  }
  return o;
}

function normalizePhiPatientReferralSourceField(
  rec: PhiPatientApiResponse
): void {
  const rs = rec.referralSource;
  if (rs != null && typeof rs === "object") {
    const rsId = String(
      (rs as Record<string, unknown>)["id"] ??
        (rs as Record<string, unknown>)["value"] ??
        ""
    ).trim();
    if (rsId) {
      rec.referralSource = rsId;
    }
  }
}

/** Normalize PHI patients API JSON into a typed patient record with `id` set. */
export function parsePhiPatientApiResponse(raw: unknown): PhiPatientApiResponse {
  const body = phiPatientRecordFromUnknown(raw);
  const id = String(
    body["id"] ?? body["patientId"] ?? body["patient_id"] ?? ""
  ).trim();
  const rec = { ...body, id } as PhiPatientApiResponse;
  normalizePhiPatientReferralSourceField(rec);
  return rec;
}

/**
 * POST/PATCH often return `null`, `{}`, or only `{ id }`. Merge the request payload so the UI
 * still has lead/queue/referral fields when the network body is empty.
 */
export function coalescePhiPatientPostResponse(
  raw: unknown,
  request: PhiPatientApiRecord | Partial<Patient> | null | undefined
): PhiPatientApiResponse {
  const fromApi = parsePhiPatientApiResponse(raw);
  const fromReq = request
    ? (parsePhiPatientApiResponse(request) as PhiPatientApiResponse)
    : ({} as PhiPatientApiResponse);
  const pick = <K extends keyof PhiPatientApiRecord>(key: K): PhiPatientApiRecord[K] | undefined => {
    const apiVal = fromApi[key];
    if (apiVal != null && String(apiVal).trim() !== "") {
      return apiVal;
    }
    return fromReq[key];
  };
  const merged: PhiPatientApiResponse = {
    ...fromReq,
    ...fromApi,
    id: (fromApi.id || fromReq.id || "").trim(),
    firstName: pick("firstName") ?? fromApi.firstName ?? fromReq.firstName,
    lastName: pick("lastName") ?? fromApi.lastName ?? fromReq.lastName,
    dob: pick("dob") ?? fromApi.dob ?? fromReq.dob,
    phone: pick("phone") ?? fromApi.phone ?? fromReq.phone,
    referralId: pick("referralId"),
    queId: pick("queId") ?? pick("que_id"),
    que_id: pick("que_id") ?? pick("queId"),
    leadStatus: pick("leadStatus"),
    formStatus: pick("formStatus"),
    formProduct: pick("formProduct"),
    service: pick("service"),
    referralSource:
      fromApi.referralSource != null && String(fromApi.referralSource).trim() !== ""
        ? fromApi.referralSource
        : fromReq.referralSource,
    referral_source:
      (fromApi.referral_source || "").trim() ||
      (fromReq.referral_source || "").trim() ||
      (typeof fromApi.referralSource === "string"
        ? fromApi.referralSource
        : "") ||
      (typeof fromReq.referralSource === "string" ? fromReq.referralSource : "") ||
      undefined,
  };
  const preferRequest = <K extends keyof PhiPatientApiRecord>(key: K): void => {
    const reqVal = fromReq[key];
    if (reqVal != null && String(reqVal).trim() !== "") {
      merged[key] = reqVal;
    }
  };
  preferRequest("height");
  preferRequest("weight");
  preferRequest("measuredOn");
  (
    [
      "pharmacyName",
      "pharmacyPhone",
      "pharmacyFax",
      "pharmacyAddress",
      "pharmacyCity",
      "pharmacyState",
      "pharmacyZip",
      "primaryCareName",
      "primaryCarePhone",
      "primaryCareAddress",
      "primaryCareCity",
      "primaryCareState",
      "primaryCareZip",
    ] as const
  ).forEach((key) => preferRequest(key));
  normalizePhiPatientReferralSourceField(merged);
  return merged;
}

export enum ContactType {
  Primary = 'primary',
  Emergency = 'emergency',
  Email = 'email',
  Advocate = 'advocate'
}

export interface PatientContact {
  id?: string;
  patientId: string;
  type: ContactType;
  contact: string;
  comment: string;
}

export class SelectedEncounterCache {
  patient: Patient;
  appointment: Appointment;
  physician: Provider;
  documents: PatientDoc[];
  schedule: EncounterSchedule;
  procedure: Procedure;
  priorAuth: PriorAuth;
  coverages: Coverage[];
  checklistFlags: AppointmentChecklist;
  checklistQuestions: ChecklistQuestionsCache[];
  checklistValues: ReferralChecklist;
  labs: OldLabInfo[];
  chiefComplaint: string;
}
